import type { ReactElement } from "react"
import styled from "styled-components"
import type { LibraryLayout } from "./PocOptionsContext"
import { usePocOptions } from "./PocOptionsContext"
import { PocToggle } from "./PocToggle"

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const StackedField = styled(Field)`
  flex-direction: column;
  align-items: start;
  gap: 0;
`

const Hint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textSecondary};
  white-space: nowrap;
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
  white-space: nowrap;
`

const Select = styled.select`
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.color.controlSurface};
  font-size: ${({ theme }) => theme.fontSize.sm};
`

export const PocOptionsPanel = (): ReactElement => {
  const { layout, dataSources, cardColor, setOption } = usePocOptions()

  return (
    <Row data-qa-hook="poc-options-panel">
      <Field>
        <Label htmlFor="poc-option-layout">Layout</Label>
        <Select
          id="poc-option-layout"
          value={layout}
          data-qa-hook="poc-option-layout"
          onChange={event => setOption("layout", event.target.value as LibraryLayout)}
        >
          <option value="rail">Rails</option>
          <option value="grid">Grid</option>
        </Select>
      </Field>

      <Field>
        <PocToggle
          id="poc-option-card-color"
          label="Card color"
          checked={cardColor}
          qaHook="poc-option-card-color"
          onChange={next => setOption("cardColor", next)}
        />
      </Field>

      <StackedField>
        <PocToggle
          id="poc-option-data-sources"
          label="Data sources"
          checked={dataSources}
          disabled={layout !== "grid"}
          qaHook="poc-option-data-sources"
          onChange={next => setOption("dataSources", next)}
        />
        <Hint data-qa-hook="poc-option-data-sources-hint">Grid layout only</Hint>
      </StackedField>
    </Row>
  )
}
